/**
 * The board's keys (US-027, Design reference § Keyboard): what the `?` dialog
 * lists, and where a key moves the focus. Both read the board as a grid of
 * card ids, one list per column from left to right, each top to bottom as
 * drawn; with swimlanes a column runs down through every lane that is open.
 */

/** One row of the `?` dialog: the keys (any of them works) and what they do. */
export interface Shortcut {
  readonly keys: readonly string[];
  readonly does: string;
}

/** Every key the board answers to; the card keys rest while the calendar is on show. */
export const BOARD_SHORTCUTS: readonly Shortcut[] = [
  { keys: ['j', '↓'], does: 'Next card in the column' },
  { keys: ['k', '↑'], does: 'Previous card in the column' },
  { keys: ['h', '←'], does: 'Same position in the column to the left' },
  { keys: ['l', '→'], does: 'Same position in the column to the right' },
  { keys: ['1–7'], does: 'First card of that column' },
  { keys: ['Enter'], does: 'Open the drawer' },
  { keys: ['Esc'], does: 'Close the drawer or the quick add, else clear the selection' },
  { keys: ['o'], does: 'Open the full page' },
  { keys: ['a'], does: "The card's primary action" },
  { keys: ['m'], does: 'The Move menu' },
  { keys: ['x'], does: 'Select / deselect' },
  { keys: ['n'], does: 'Quick-add ideas' },
  { keys: ['e'], does: 'Open the drawer with the campaign field focused' },
  { keys: ['/'], does: 'Focus search' },
  { keys: ['.'], does: 'Next card that needs you' },
  { keys: ['v'], does: 'Board / Calendar' },
  { keys: ['[', ']'], does: 'Calendar: previous / next month or week' },
  { keys: ['t'], does: 'Calendar: today' },
  { keys: ['?'], does: 'This list' },
  { keys: ['g', 'k'], does: 'The board, from anywhere' },
];

export type Grid = readonly (readonly string[])[];

function locate(grid: Grid, cardId: string | null): { column: number; row: number } | null {
  if (cardId === null) return null;
  for (const [column, list] of grid.entries()) {
    const row = list.indexOf(cardId);
    if (row !== -1) return { column, row };
  }
  return null;
}

/** The first card on the board: the top of the leftmost column that has one. */
export function firstCard(grid: Grid): string | null {
  for (const list of grid) if (list.length > 0) return list[0] ?? null;
  return null;
}

/** `j`/`k`: one card down or up the column, stopping at either end. */
export function stepInColumn(grid: Grid, cardId: string | null, delta: 1 | -1): string | null {
  const at = locate(grid, cardId);
  if (at === null) return firstCard(grid);
  const list = grid[at.column] ?? [];
  return list[Math.min(Math.max(at.row + delta, 0), list.length - 1)] ?? null;
}

/**
 * `h`/`l`: the nearest column that way with a card in it, at the same position
 * or its last card when it is shorter. At the edge the focus stays.
 */
export function stepSideways(grid: Grid, cardId: string | null, delta: 1 | -1): string | null {
  const at = locate(grid, cardId);
  if (at === null) return firstCard(grid);
  for (let column = at.column + delta; column >= 0 && column < grid.length; column += delta) {
    const list = grid[column] ?? [];
    if (list.length > 0) return list[Math.min(at.row, list.length - 1)] ?? null;
  }
  return cardId;
}

/** `1`–`7`: the first card of that column (0-based here), `null` when it is empty. */
export function firstOfColumn(grid: Grid, column: number): string | null {
  return grid[column]?.[0] ?? null;
}

/**
 * `.`: the next card after this one, in board order (columns left to right,
 * cards top to bottom, wrapping), that needs you. The focused card itself
 * comes last, so with only it left it is the answer; `null` when none does.
 */
export function nextNeedingYou(grid: Grid, cardId: string | null, needs: (cardId: string) => boolean): string | null {
  const order = grid.flat();
  const start = cardId === null ? -1 : order.indexOf(cardId);
  for (let step = 1; step <= order.length; step += 1) {
    const candidate = order[(start + step + order.length) % order.length];
    if (candidate !== undefined && needs(candidate)) return candidate;
  }
  return null;
}
